// src/pages/surveys/components/SurveySettingsPanel.js
import React, { useRef, useEffect } from 'react';
import { ROLES, ROLE_LABELS } from '../../../config/roles';

// ── Inject placeholder CSS once ──────────────────────────────────
let _styleInjected = false;
function injectStyle() {
  if (_styleInjected || typeof document === 'undefined') return;
  _styleInjected = true;
  const el = document.createElement('style');
  el.textContent = `[contenteditable][data-placeholder]:empty:before{content:attr(data-placeholder);color:#aaa;pointer-events:none;}`;
  document.head.appendChild(el);
}

// ── Mini rich-text editor (B / I / U / ↵) ───────────────────────
function RichTextEditor({ value, onChange, placeholder, minHeight = 72 }) {
  const ref = useRef(null);
  injectStyle();

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const exec = (cmd) => {
    ref.current?.focus();
    document.execCommand(cmd, false, null);
    onChange(ref.current?.innerHTML || '');
  };

  const handleInput = () => onChange(ref.current?.innerHTML || '');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertLineBreak', false, null);
    }
  };

  const btn = {
    padding: '2px 7px', fontSize: 12, fontWeight: 700,
    background: 'var(--ka-gray-100)', color: 'var(--ka-gray-700)',
    border: '1px solid var(--ka-gray-200)', borderRadius: 4,
    cursor: 'pointer', fontFamily: 'var(--font-body)', lineHeight: '18px',
    flexShrink: 0,
  };

  return (
    <div style={{ border: '1px solid var(--ka-gray-200)', borderRadius: 6, overflow: 'hidden' }}>
      <div style={{
        display: 'flex', gap: 3, padding: '4px 6px',
        background: 'var(--ka-gray-50)', borderBottom: '1px solid var(--ka-gray-200)',
      }}>
        <button type="button" style={btn} onMouseDown={e => { e.preventDefault(); exec('bold'); }} title="Bold"><b>B</b></button>
        <button type="button" style={btn} onMouseDown={e => { e.preventDefault(); exec('italic'); }} title="Italic"><i>I</i></button>
        <button type="button" style={btn} onMouseDown={e => { e.preventDefault(); exec('underline'); }} title="Underline"><u>U</u></button>
        <button type="button" style={{ ...btn, fontSize: 10 }} onMouseDown={e => { e.preventDefault(); exec('insertLineBreak'); }} title="Line break">↵</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        style={{
          padding: '8px 10px', minHeight,
          fontSize: 13, fontFamily: 'var(--font-body)', lineHeight: 1.5,
          color: 'var(--ka-navy)', outline: 'none',
          background: 'var(--ka-white)', resize: 'vertical', overflowY: 'auto',
        }}
      />
    </div>
  );
}

const sp = {
  wrap: { padding: 16 },
  section: { marginBottom: 20 },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--ka-gray-400)',
    marginBottom: 10,
    display: 'block',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 0',
    borderBottom: '1px solid var(--ka-gray-100)',
  },
  toggleLabel: { fontSize: 13, color: 'var(--ka-gray-800)' },
  toggleSub: { fontSize: 11, color: 'var(--ka-gray-400)', marginTop: 1 },
  checkbox: { width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--ka-navy)' },
  label: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
    textTransform: 'uppercase', color: 'var(--ka-gray-400)',
    marginBottom: 6, marginTop: 14, display: 'block',
  },
  input: {
    width: '100%', padding: '8px 10px', fontSize: 13,
    border: '1px solid var(--ka-gray-200)', borderRadius: 6,
    color: 'var(--ka-navy)', background: 'var(--ka-white)',
    fontFamily: 'var(--font-body)', outline: 'none', boxSizing: 'border-box',
  },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginTop: 8,
  },
  roleChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 10px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: '1.5px solid',
    margin: '4px 4px 0 0',
    fontFamily: 'var(--font-body)',
    transition: 'all 0.1s',
  },
};

export default function SurveySettingsPanel({
  settings, onChange,
  title, onTitleChange,
  description, onDescriptionChange,
}) {
  const set = (key, val) => onChange({ ...settings, [key]: val });

  const toggleRole = (role) => {
    const current = settings.accessRoles || ['admin'];
    const updated = current.includes(role)
      ? current.filter(r => r !== role)
      : [...current, role];
    if (!updated.includes('admin')) updated.push('admin');
    set('accessRoles', updated);
  };

  const authRequired = settings.authRequired === true;

  return (
    <div style={sp.wrap}>

      {/* Survey Title & Description */}
      <div style={sp.section}>
        <span style={sp.sectionTitle}>Survey Info</span>
        <span style={{ ...sp.label, marginTop: 0 }}>Title</span>
        <input
          style={sp.input}
          value={title || ''}
          onChange={e => onTitleChange(e.target.value)}
          placeholder="Survey Title"
        />
        <span style={sp.label}>Description</span>
        <RichTextEditor
          value={description || ''}
          onChange={onDescriptionChange}
          placeholder="Survey description — supports bold, italic, underline and line breaks"
          minHeight={72}
        />
      </div>

      {/* Who Can Respond */}
      <div style={sp.section}>
        <span style={sp.sectionTitle}>Who Can Respond</span>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 0', borderBottom: '1px solid var(--ka-gray-100)' }}>
            <input
              type="radio"
              name="authRequired"
              checked={!authRequired}
              onChange={() => set('authRequired', false)}
              style={{ marginTop: 3, accentColor: 'var(--ka-navy)' }}
            />
            <div>
              <div style={{ fontSize: 13, color: 'var(--ka-gray-800)', fontWeight: 600 }}>Anyone with the link</div>
              <div style={{ fontSize: 11, color: 'var(--ka-gray-400)', marginTop: 1 }}>Public — no sign-in required</div>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 0' }}>
            <input
              type="radio"
              name="authRequired"
              checked={authRequired}
              onChange={() => set('authRequired', true)}
              style={{ marginTop: 3, accentColor: 'var(--ka-navy)' }}
            />
            <div>
              <div style={{ fontSize: 13, color: 'var(--ka-gray-800)', fontWeight: 600 }}>King's Academy accounts only</div>
              <div style={{ fontSize: 11, color: 'var(--ka-gray-400)', marginTop: 1 }}>Requires Google sign-in · name &amp; email collected automatically</div>
            </div>
          </label>
        </div>
      </div>

      {/* Respondent Info — only shown for public surveys */}
      {!authRequired && (
        <div style={sp.section}>
          <span style={sp.sectionTitle}>Respondent Info</span>

          <div style={sp.toggle}>
            <div>
              <div style={sp.toggleLabel}>Collect Name</div>
            </div>
            <input
              type="checkbox"
              style={sp.checkbox}
              checked={settings.collectName}
              onChange={e => set('collectName', e.target.checked)}
            />
          </div>
          {settings.collectName && (
            <div style={{ ...sp.toggle, paddingLeft: 12 }}>
              <div style={{ ...sp.toggleLabel, fontSize: 12, color: 'var(--ka-gray-400)' }}>Name Required</div>
              <input
                type="checkbox"
                style={sp.checkbox}
                checked={settings.nameRequired}
                onChange={e => set('nameRequired', e.target.checked)}
              />
            </div>
          )}

          <div style={sp.toggle}>
            <div>
              <div style={sp.toggleLabel}>Collect Email</div>
            </div>
            <input
              type="checkbox"
              style={sp.checkbox}
              checked={settings.collectEmail}
              onChange={e => set('collectEmail', e.target.checked)}
            />
          </div>
          {settings.collectEmail && (
            <div style={{ ...sp.toggle, paddingLeft: 12 }}>
              <div style={{ ...sp.toggleLabel, fontSize: 12, color: 'var(--ka-gray-400)' }}>Email Required</div>
              <input
                type="checkbox"
                style={sp.checkbox}
                checked={settings.emailRequired}
                onChange={e => set('emailRequired', e.target.checked)}
              />
            </div>
          )}
        </div>
      )}

      {/* Behavior */}
      <div style={sp.section}>
        <span style={sp.sectionTitle}>Behavior</span>

        <div style={sp.toggle}>
          <div>
            <div style={sp.toggleLabel}>Allow Multiple Submissions</div>
            <div style={sp.toggleSub}>Per browser/device</div>
          </div>
          <input
            type="checkbox"
            style={sp.checkbox}
            checked={settings.allowMultiple}
            onChange={e => set('allowMultiple', e.target.checked)}
          />
        </div>

        <div style={sp.toggle}>
          <div>
            <div style={sp.toggleLabel}>Show Progress Bar</div>
          </div>
          <input
            type="checkbox"
            style={sp.checkbox}
            checked={settings.showProgressBar}
            onChange={e => set('showProgressBar', e.target.checked)}
          />
        </div>

        <span style={sp.label}>Confirmation Message</span>
        <textarea
          style={{ ...sp.input, minHeight: 72, resize: 'vertical' }}
          value={settings.confirmationMessage}
          onChange={e => set('confirmationMessage', e.target.value)}
          placeholder="Message shown after submission"
        />
      </div>

      {/* Design */}
      <div style={sp.section}>
        <span style={sp.sectionTitle}>Design</span>

        <div style={sp.toggle}>
          <div>
            <div style={sp.toggleLabel}>Show King's Academy Logo</div>
            <div style={sp.toggleSub}>Displays the KA shield in the survey header</div>
          </div>
          <input
            type="checkbox"
            style={sp.checkbox}
            checked={settings.showLogo !== false}
            onChange={e => set('showLogo', e.target.checked)}
          />
        </div>

        <span style={sp.label}>Accent Color</span>
        <div style={sp.colorRow}>
          {['#1a2744', '#c8973a', '#2563eb', '#16a34a', '#dc2626', '#7c3aed', '#0891b2'].map(color => (
            <div
              key={color}
              style={{
                width: 28, height: 28, borderRadius: '50%',
                background: color, cursor: 'pointer',
                border: settings.accentColor === color ? '3px solid var(--ka-navy)' : '2px solid transparent',
                outline: settings.accentColor === color ? '2px solid white' : 'none',
                outlineOffset: -4,
                boxSizing: 'border-box',
                transition: 'transform 0.1s',
              }}
              onClick={() => set('accentColor', color)}
            />
          ))}
          <input
            type="color"
            value={settings.accentColor}
            onChange={e => set('accentColor', e.target.value)}
            style={{ width: 28, height: 28, padding: 0, border: 'none', cursor: 'pointer', borderRadius: '50%' }}
            title="Custom color"
          />
        </div>
      </div>

      {/* Results Access */}
      <div style={sp.section}>
        <span style={sp.sectionTitle}>Results Access</span>
        <div style={{ fontSize: 12, color: 'var(--ka-gray-400)', marginBottom: 8, lineHeight: 1.5 }}>
          Who can view responses for this survey:
        </div>
        <div>
          {Object.entries(ROLE_LABELS).map(([role, label]) => {
            const active = (settings.accessRoles || ['admin']).includes(role);
            const isAdmin = role === 'admin';
            return (
              <button
                key={role}
                style={{
                  ...sp.roleChip,
                  background: active ? 'var(--ka-navy)' : 'transparent',
                  color: active ? 'var(--ka-white)' : 'var(--ka-gray-400)',
                  borderColor: active ? 'var(--ka-navy)' : 'var(--ka-gray-200)',
                  opacity: isAdmin ? 0.6 : 1,
                  cursor: isAdmin ? 'not-allowed' : 'pointer',
                }}
                onClick={() => !isAdmin && toggleRole(role)}
                disabled={isAdmin}
              >
                {active ? '✓' : '+'} {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}